import { createRouter, createWebHistory } from 'vue-router'

import MainLayout from '@/components/Layouts/MainLayout.vue'
import { PAGE_NAME_MAPPING } from '@/components/Navigation/Navigation.helpers'
import { useAuthStore } from '@/stores/auth.stores'
import { useUserStore } from '@/stores/user'
import AdvertiserView from '@/views/AdvertiserView.vue'
import CampaignCopy from '@/views/CampaignCopy.vue'
import CampaignCreation from '@/views/CampaignCreation.vue'
import CampaignEditing from '@/views/CampaignEditing.vue'
import CampaignsView from '@/views/CampaignsView.vue'
import ClientsView from '@/views/ClientsView.vue'
import CreatorDetailView from '@/views/CreatorDetailView.vue'
import CreatorsView from '@/views/CreatorsView.vue'
import DashboardView from '@/views/DashboardView.vue'
import IdentityEditing from '@/views/IdentityEditing.vue'
import LogoutView from '@/views/LogoutView.vue'
import NoAccess from '@/views/NoAccess.vue'
import ReturnAdCreation from '@/views/ReturnAdCreation.vue'
import ReturnAdEditing from '@/views/ReturnAdEditing.vue'
import ReturnAdsView from '@/views/ReturnAdsView.vue'
import SegmentsView from '@/views/SegmentsView.vue'

export const routes = [
  {
    path: '/logout',
    name: 'logout',
    component: LogoutView,
  },
  {
    path: '/',
    name: 'main-layout',
    component: MainLayout,
    beforeEnter: async () => {
      const authStore = useAuthStore()
      const userStore = useUserStore()

      const isAuthenticated = await authStore.authenticate()

      if (!isAuthenticated) {
        return { name: 'logout' }
      }

      await userStore.initialize()

      if (!userStore.user) {
        return { name: 'logout' }
      }

      if (!userStore.user.isAdmin) {
        return { name: 'no-access' }
      }

      return true
    },
    children: [
      {
        path: '/',
        name: 'dashboard',
        component: DashboardView,
        meta: {
          title: PAGE_NAME_MAPPING.dashboard,
        },
      },
      {
        path: '/campaigns',
        name: 'Campaigns',
        children: [
          {
            path: '',
            component: CampaignsView,
            name: 'campaigns',
            meta: {
              title: PAGE_NAME_MAPPING.campaigns,
            },
          },
          {
            path: 'create',
            component: CampaignCreation,
            name: 'createCampaign',
            meta: {
              title: PAGE_NAME_MAPPING.createCampaign,
            },
          },
          {
            path: ':id',
            component: CampaignEditing,
            name: 'editCampaign',
            meta: {
              title: PAGE_NAME_MAPPING.editCampaign,
            },
          },
          {
            path: 'copy',
            component: CampaignCopy,
            name: 'copyCampaign',
            meta: {
              title: PAGE_NAME_MAPPING.copyCampaign,
            },
          },
        ],
      },
      {
        path: '/return-ads',
        children: [
          {
            path: '',
            component: ReturnAdsView,
            name: 'returnAds',
            meta: {
              title: PAGE_NAME_MAPPING.returnAds,
            },
          },
          {
            path: 'create',
            component: ReturnAdCreation,
            name: 'createReturnAd',
            meta: {
              title: PAGE_NAME_MAPPING.createReturnAd,
            },
          },
          {
            path: ':id',
            component: ReturnAdEditing,
            name: 'editReturnAd',
            meta: {
              title: PAGE_NAME_MAPPING.editReturnAd,
            },
          },
        ],
      },
      {
        path: '/creators',
        children: [
          {
            path: '',
            component: CreatorsView,
            name: 'creators',
            meta: {
              title: PAGE_NAME_MAPPING.creators,
            },
          },
          {
            path: ':id/:tab?',
            component: CreatorDetailView,
            name: 'viewCreator',
            meta: {
              title: PAGE_NAME_MAPPING.viewCreator,
            },
          },
          {
            path: ':id/identity',
            component: IdentityEditing,
            name: 'identity',
            meta: {
              title: PAGE_NAME_MAPPING.identity,
            },
          },
        ],
      },
      {
        path: '/clients',
        children: [
          {
            path: '',
            component: ClientsView,
            name: 'clients',
            meta: {
              title: PAGE_NAME_MAPPING.clients,
            },
          },
        ],
      },
      {
        path: '/advertisers/:id/reports/:uuid',
        name: 'advertiserReportEditor',
        component: () => import('@/views/AdvertiserReportEditorView.vue'),
        meta: {
          title: PAGE_NAME_MAPPING.advertiserReportEditor,
        },
      },
      {
        path: '/advertisers/:id/:tab?',
        name: 'viewAdvertiser',
        component: AdvertiserView,
        meta: {
          title: PAGE_NAME_MAPPING.viewAdvertiser,
        },
      },
      {
        path: '/404',
        name: 'notFound',
        component: () => import('@/views/NotFound.vue'),
        meta: {
          title: PAGE_NAME_MAPPING.notFound,
        },
      },
      {
        path: '/content-library',
        name: 'contentLibrary',
        component: () => import('@/views/ContentLibraryView.vue'),
        meta: {
          title: PAGE_NAME_MAPPING.contentLibrary,
        },
      },
      {
        path: '/marketing-notifications',
        name: 'marketingNotifications',
        component: () => import('@/views/MarketingNotificationsView.vue'),
        meta: { title: PAGE_NAME_MAPPING.marketingNotifications },
      },
      {
        path: '/marketing-notifications/create',
        name: 'marketingNotificationCreate',
        component: () => import('@/views/MarketingNotificationCreation.vue'),
        meta: { title: PAGE_NAME_MAPPING.marketingNotificationCreate },
      },
      {
        path: '/marketing-notifications/:id/edit',
        name: 'marketingNotificationEdit',
        component: () => import('@/views/MarketingNotificationEditing.vue'),
        meta: { title: PAGE_NAME_MAPPING.marketingNotificationEdit },
      },
      {
        path: '/segments',
        name: 'segments',
        component: SegmentsView,
        meta: {
          title: PAGE_NAME_MAPPING.segments,
        },
      },
      {
        path: '/segments/create',
        name: 'segmentCreate',
        component: () => import('@/views/SegmentCreation.vue'),
        meta: {
          title: PAGE_NAME_MAPPING.segmentCreate,
        },
      },
      {
        path: '/segments/:id/edit',
        name: 'segmentEdit',
        component: () => import('@/views/SegmentEditing.vue'),
        meta: {
          title: PAGE_NAME_MAPPING.segmentEdit,
        },
      },
    ],
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: NoAccess,
    meta: { title: 'You have no access' },
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach((to) => {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
  document.title = to.meta?.title ? `${to.meta.title} | Metapic Admin` : 'Metapic Admin'
  return true
})

export default router
